import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { Link, Navigate } from 'react-router-dom';
import '../styles/Landing.css';
import SectionHelpModal from '../components/common/SectionHelpModal';
import AppTour from '../components/common/AppTour';
import PublicNavbar from '../components/layout/PublicNavbar';
import { FiBox, FiUsers, FiTrendingUp, FiLogOut, FiMenu, FiX } from 'react-icons/fi';
import quotationsService from '../services/quotations.service';
import { Badge } from 'react-bootstrap';

const Landing = () => {
    const { isAuthenticated, user, logout } = useAuth();
    const { refreshTrigger } = useNotifications();
    const [pendingCount, setPendingCount] = React.useState(0);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [runTour, setRunTour] = useState(false); // Estado para disparar el tour manualmente
    const [finalStepOnly, setFinalStepOnly] = useState(false);

    let currentTourSteps = [];

    if (finalStepOnly) {
        currentTourSteps = [{
            element: '#tour-cotizaplus-btn',
            intro: '<strong>Tour Completado</strong>Este es tu Módulo Estrella. Haz clic aquí para entrar y empezar a operar.',
            position: 'right',
        }];
    } else {
        currentTourSteps = [
            {
                element: '#tour-welcome',
                intro: '<strong>Bienvenido a IdevPlus</strong>Este breve recorrido te mostrará cómo usar la plataforma. Tendrás el control de tus ventas, tu equipo y los datos de tu empresa desde una sola pantalla.',
                position: 'bottom',
            },
            {
                element: '.user-dropdown', // Clase de Avatar/Dropdown en GlobalHeader
                intro: '<strong>Tu Menú de Opciones</strong>Haz clic en la flecha junto a tu nombre (arriba a la derecha) para ajustar los datos de tu empresa, invitar personal y cerrar sesión.',
                position: 'left',
            }
        ];

        if (user?.rol === 'ADMIN') {
            currentTourSteps.push({
                element: '#nav-empleados',
                intro: '<strong>Gestión de Personal</strong>Desde aquí darás de alta a todo tu personal y gestionarás sus permisos de acceso.',
                position: 'bottom',
            });
            currentTourSteps.push({
                element: '#nav-configuraciones',
                intro: '<strong>Configuración</strong>Aquí configuras el logo y los datos fiscales de tu empresa que aparecerán en cada cotización.',
                position: 'bottom',
            });
        }
    }

    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('tour') === 'final') {
            setFinalStepOnly(true);
            setRunTour(true);
            window.history.replaceState({}, document.title, window.location.pathname);
            return; // No seguir para evitar conflicto con auto-tour
        }

        if (isAuthenticated && user) {
            loadStats();
            const interval = setInterval(loadStats, 30000);

            // Auto-activar el tour si es la primera vez que el usuario entra
            const tourKey = `idevplus_tour_visto_${user.id}`;
            if (!localStorage.getItem(tourKey)) {
                // Pequeño delay para que la página cargue antes de mostrar el tour
                const tourTimer = setTimeout(() => {
                    setRunTour(true);
                }, 1200);
                return () => {
                    clearInterval(interval);
                    clearTimeout(tourTimer);
                };
            }

            return () => clearInterval(interval);
        }
    }, [isAuthenticated, user, refreshTrigger]);

    const loadStats = async () => {
        try {
            const data = await quotationsService.getPendingStats();
            setPendingCount(data.count !== undefined ? data.count : (data.pendientes || 0));
        } catch (err) {
            console.error('Error loading stats:', err);
        }
    };

    // Redirigir a cambio de contraseña si es obligatorio
    if (isAuthenticated && user?.cambio_password_obligatorio) {
        return <Navigate to="/change-password" replace />;
    }

    if (isAuthenticated) {
        return (
            <div className="launcher-container" style={{ minHeight: 'auto', backgroundColor: 'transparent', width: '100%', flex: 1 }}>
                {/* 🌟 Capa del Tour Guiado Inyectada 🌟 */}
                {runTour && (
                    <AppTour
                        steps={currentTourSteps}
                        runTour={runTour}
                        setRunTour={setRunTour}
                        skipUrl={null}
                        tourOptions={{ doneLabel: finalStepOnly ? '¡Terminar Tutorial!' : 'Continuar a Solicitudes' }}
                        onTourComplete={() => {
                            // Marcar el tour como visto para este usuario
                            if (user?.id) {
                                localStorage.setItem(`idevplus_tour_visto_${user.id}`, '1');
                            }
                            if (!finalStepOnly) {
                                window.location.href = '/app/solicitudes?tour=solicitudes';
                            } else {
                                setFinalStepOnly(false);
                                setRunTour(false);
                            }
                        }}
                        onTourExit={() => {
                            // Marcar también como visto al saltar (para no repetirlo)
                            if (user?.id) {
                                localStorage.setItem(`idevplus_tour_visto_${user.id}`, '1');
                            }
                            setFinalStepOnly(false);
                        }}
                    />
                )}

                <div className="launcher-content" style={{ padding: '2rem', width: '100%', maxWidth: '1000px', margin: '0 auto' }}>

                    <div id="tour-welcome" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h1 className="launcher-title" style={{ marginBottom: 0 }}>Mis Aplicaciones</h1>
                        <SectionHelpModal
                            title="¿Qué es este panel principal?"
                            content={
                                <div>
                                    <p>¡Bienvenido a IdevPlus! Esta es la pantalla de inicio central de tu cuenta. Desde aquí puedes entrar a los distintos sistemas:</p>
                                    <ul style={{ paddingLeft: '20px', marginTop: '15px' }}>
                                        <li style={{ marginBottom: '10px' }}><strong>CotizaPlus:</strong> Haz clic aquí para entrar. Sirve para crear presupuestos y cotizaciones en PDF, registrar tus clientes y productos, y enviar precios autorizados.</li>
                                        <li style={{ marginBottom: '10px' }}><strong>Empleados, Solicitudes y Configuraciones:</strong> Nota los botones en la parte superior derecha de esta pantalla; allí configuras tu empresa y gestionas a tu personal.</li>
                                        <li><strong>Próximamente:</strong> Más adelante se activarán aplicaciones para que manejes tu Inventario y CRM directamente desde aquí.</li>
                                    </ul>
                                </div>
                            }
                        />
                        <button
                            onClick={() => setRunTour(true)}
                            title="Iniciar recorrido guiado"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: 'linear-gradient(135deg, #6366f1, #818cf8)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '100px',
                                padding: '6px 16px',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
                                transition: 'all 0.2s ease',
                                letterSpacing: '0.2px',
                                flexShrink: 0
                            }}
                            onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(99,102,241,0.45)'; }}
                            onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(99,102,241,0.35)'; }}
                        >
                            <FiTrendingUp size={14} /> Iniciar Recorrido
                        </button>
                    </div>
                    <p className="launcher-subtitle">Selecciona una herramienta para comenzar</p>

                    <div className="app-grid">
                        {/* CotizaPlus - Active */}
                        <Link to="/app/cotizaplus" id="tour-cotizaplus-btn" className="app-card active" style={{ position: 'relative' }}>
                            {pendingCount > 0 && (
                                <Badge bg="danger" pill style={{ position: 'absolute', top: '20px', right: '20px', fontSize: '0.9rem', padding: '0.4em 0.6em', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                                    {pendingCount}
                                </Badge>
                            )}
                            <div className="app-icon quote-icon">
                                <FiTrendingUp size={40} />
                            </div>
                            <div className="app-info">
                                <h3>CotizaPlus</h3>
                                <p>Gestión de cotizaciones y ventas</p>
                            </div>
                            <div className="app-status">Activo</div>
                        </Link>

                        {/* CRM - Coming Soon */}
                        <div className="app-card disabled">
                            <div className="app-icon crm-icon">
                                <FiUsers size={40} />
                            </div>
                            <div className="app-info">
                                <h3>CRM Plus</h3>
                                <p>Gestión de relaciones con clientes</p>
                            </div>
                            <div className="app-status coming-soon">Próximamente</div>
                        </div>

                        {/* Inventario - Coming Soon */}
                        <div className="app-card disabled">
                            <div className="app-icon inventory-icon">
                                <FiBox size={40} />
                            </div>
                            <div className="app-info">
                                <h3>Inventario Plus</h3>
                                <p>Control de stock y logística</p>
                            </div>
                            <div className="app-status coming-soon">Próximamente</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Unauthenticated View - Landing Page
    return (
        <div className="landing-container">
            <PublicNavbar />

            <header className="landing-hero">
                <div className="hero-content">
                    <h1>La Suma para tu <span className="highlight">Negocio</span></h1>
                    <p>Cotizaciones, CRM e Inventario en una plataforma diseñada para PYMEs. Simple, potente y accesible.</p>
                    <div className="hero-buttons">
                        <Link to="/register" className="btn-cta">Prueba Gratis</Link>
                        <Link to="/pricing" className="btn-secondary">Ver Precios</Link>
                    </div>
                </div>
                <div className="hero-visual">
                    <div className="visual-placeholder">
                        <div className="floating-circle circle-1"></div>
                        <div className="floating-circle circle-2"></div>

                        <div className="hero-card card-1">
                            <div className="card-icon icon-blue">
                                <FiTrendingUp />
                            </div>
                            <div className="card-text">
                                <h4>Ventas al día</h4>
                                <p>+15% vs mes anterior</p>
                            </div>
                        </div>

                        <div className="hero-card card-2">
                            <div className="card-icon icon-pink">
                                <FiUsers />
                            </div>
                            <div className="card-text">
                                <h4>Nuevos Clientes</h4>
                                <p>5 leads pendientes</p>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Marquee de servicios */}
            <div className="services-marquee">
                <div className="marquee-track">
                    <div className="marquee-content">
                        <span className="marquee-item"><span className="marquee-star">✦</span> Cotizaciones Profesionales</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> CRM Integrado</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> Control de Inventario</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> Envío por WhatsApp</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> Reportes en Tiempo Real</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> Portal de Autoatención</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> Gestión de Clientes</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> Plantillas de Correo</span>
                    </div>
                    <div className="marquee-content" aria-hidden="true">
                        <span className="marquee-item"><span className="marquee-star">✦</span> Cotizaciones Profesionales</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> CRM Integrado</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> Control de Inventario</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> Envío por WhatsApp</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> Reportes en Tiempo Real</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> Portal de Autoatención</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> Gestión de Clientes</span>
                        <span className="marquee-item"><span className="marquee-star">✦</span> Plantillas de Correo</span>
                    </div>
                </div>
            </div>

            <section className="features-section">
                <h2>Todo lo que necesitas para crecer</h2>
                <div className="features-grid">
                    <div className="feature-item">
                        <div className="feature-icon-wrapper">
                            <FiTrendingUp size={28} />
                        </div>
                        <h3>Cotizaciones Rápidas</h3>
                        <p>Crea propuestas profesionales en segundos y envíalas por WhatsApp o correo.</p>
                    </div>
                    <div className="feature-item">
                        <div className="feature-icon-wrapper">
                            <FiUsers size={28} />
                        </div>
                        <h3>CRM Integrado</h3>
                        <p>Centraliza la información de tus clientes y no pierdas ninguna oportunidad de venta.</p>
                    </div>
                    <div className="feature-item">
                        <div className="feature-icon-wrapper">
                            <FiBox size={28} />
                        </div>
                        <h3>Control de Inventario</h3>
                        <p>Mantén tu stock actualizado automáticamente con cada venta realizada.</p>
                    </div>
                </div>
            </section>

            {/* Call to Action Precios */}
            <section id="precios" className="pricing-cta-section" style={{ padding: '80px 20px', background: 'linear-gradient(135deg, #f8f9fa 0%, #eef2ff 100%)', textAlign: 'center' }}>
                <h2 style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1a1a2e', marginBottom: '20px' }}>Paga solo por lo que usas</h2>
                <p style={{ fontSize: '1.2rem', color: '#666', maxWidth: '600px', margin: '0 auto 40px auto', lineHeight: '1.6' }}>
                    Nuestra plataforma es modular. Empieza probando cualquiera de nuestras herramientas <strong>GRATIS por 30 días</strong> y construye el ecosistema que tu negocio necesita.
                </p>
                <Link to="/pricing" className="btn-cta" style={{ display: 'inline-block', padding: '16px 32px', fontSize: '1.1rem', borderRadius: '50px', background: 'linear-gradient(135deg, #6366f1, #4a00e0)', color: 'white', textDecoration: 'none', fontWeight: 'bold', boxShadow: '0 8px 20px rgba(99, 102, 241, 0.3)' }}>
                    Ver Planes y Precios
                </Link>
            </section>

            <footer className="landing-footer">
                <div className="footer-content">
                    <div className="footer-logo">IdevPlus</div>
                    <p>&copy; {new Date().getFullYear()} IdevPlus. Todos los derechos reservados.</p>
                </div>
            </footer>
        </div>
    );
};

export default Landing;
